

import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  Check,
  X,
  Shield,
} from 'lucide-react';
import { api, AdminUser, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatDateTime = (dateString: string | null) => {
  if (!dateString) return 'Nunca';
  return new Date(dateString).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const roleLabels: Record<string, string> = {
  'general-admin': 'Administrador Geral',
};

export default function AdminsPage() {
  const { user: currentUser } = useAuth();
  const [search, setSearch] = React.useState('');
  const [includeInactive, setIncludeInactive] = React.useState(false);
  const [admins, setAdmins] = React.useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isDeleting, setIsDeleting] = React.useState<string | null>(null);

  const loadAdmins = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.getAdminUsers(includeInactive);
      setAdmins(data);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Erro ao carregar administradores');
    } finally {
      setIsLoading(false);
    }
  }, [includeInactive]);

  React.useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  const handleDelete = async (id: string, name: string) => {
    if (currentUser?.id === id) {
      alert('Voce nao pode excluir sua propria conta');
      return;
    }

    if (
      !confirm(
        `Tem certeza que deseja excluir o administrador "${name}"? Esta acao nao pode ser desfeita.`
      )
    ) {
      return;
    }

    try {
      setIsDeleting(id);
      await api.deleteAdminUser(id);
      setAdmins((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      const apiError = err as ApiError;
      alert(apiError.message || 'Erro ao excluir administrador');
    } finally {
      setIsDeleting(null);
    }
  };

  const filteredAdmins = admins.filter(
    (admin) =>
      admin.name.toLowerCase().includes(search.toLowerCase()) ||
      admin.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Administradores</h1>
          <p className="text-gray-500 mt-1">
            Gerencie os administradores da plataforma
          </p>
        </div>
        <Link
          to="/admins/new"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#0057FF] to-[#7C3AED] text-white font-medium text-sm hover:opacity-90 transition-opacity shadow-lg shadow-[#0057FF]/25"
        >
          <Plus className="h-5 w-5" />
          Novo Administrador
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar administrador..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-white px-12 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-[#0057FF] focus:ring-[#0057FF]"
            />
            <span className="text-sm text-gray-600">Incluir inativos</span>
          </label>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-[#0057FF]" />
            <p className="text-gray-500">Carregando administradores...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="bg-red-50 rounded-2xl border border-red-100 p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-red-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-red-800">
                Erro ao carregar administradores
              </h3>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
            <button
              onClick={loadAdmins}
              className="px-4 py-2 rounded-xl bg-red-100 text-red-700 text-sm font-medium hover:bg-red-200 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {!isLoading && !error && (
        <>
          {filteredAdmins.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12">
              <div className="flex flex-col items-center gap-4">
                <div className="h-16 w-16 rounded-2xl bg-gray-100 flex items-center justify-center">
                  <Shield className="h-8 w-8 text-gray-400" />
                </div>
                <div className="text-center">
                  <p className="font-medium text-gray-900">
                    Nenhum administrador encontrado
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {search
                      ? 'Tente buscar com outros termos'
                      : 'Comece criando seu primeiro administrador'}
                  </p>
                </div>
                {!search && (
                  <Link
                    to="/admins/new"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0057FF] text-white text-sm font-medium hover:bg-[#0057FF]/90 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Novo Administrador
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Ultimo Login
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Criado em
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Acoes
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredAdmins.map((admin) => (
                    <tr
                      key={admin.id}
                      className={`hover:bg-gray-50 transition-colors ${
                        admin.status !== 'active' ? 'opacity-60' : ''
                      }`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#0057FF] to-[#7C3AED] flex items-center justify-center">
                            <span className="text-white font-semibold text-sm">
                              {admin.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{admin.name}</p>
                            {currentUser?.id === admin.id && (
                              <span className="text-xs text-[#0057FF]">Voce</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-600">{admin.email}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2.5 py-1 rounded-lg bg-purple-50 text-purple-700 text-xs font-medium">
                          {roleLabels[admin.role] || admin.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${
                            admin.status === 'active'
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {admin.status === 'active' ? (
                            <Check className="h-3 w-3" />
                          ) : (
                            <X className="h-3 w-3" />
                          )}
                          {admin.status === 'active' ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-500">
                          {formatDateTime(admin.lastLoginAt)}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-500">
                          {formatDate(admin.createdAt)}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            to={`/admins/${admin.id}/edit`}
                            className="p-2 rounded-lg text-gray-400 hover:text-[#0057FF] hover:bg-[#0057FF]/10 transition-colors"
                          >
                            <Pencil className="h-5 w-5" />
                          </Link>
                          <button
                            onClick={() => handleDelete(admin.id, admin.name)}
                            disabled={isDeleting === admin.id || currentUser?.id === admin.id}
                            className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={
                              currentUser?.id === admin.id
                                ? 'Voce nao pode excluir sua propria conta'
                                : 'Excluir administrador'
                            }
                          >
                            {isDeleting === admin.id ? (
                              <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                              <Trash2 className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Summary */}
          {filteredAdmins.length > 0 && (
            <div className="text-sm text-gray-500 text-center">
              Mostrando{' '}
              <span className="font-medium text-gray-900">
                {filteredAdmins.length}
              </span>{' '}
              de{' '}
              <span className="font-medium text-gray-900">{admins.length}</span>{' '}
              administradores
            </div>
          )}
        </>
      )}
    </div>
  );
}
